import { api, unwrap, type ApiEnvelope } from './api';
import type { ExportFormat, Invoice, InvoiceStatus } from '@/types/invoice';

export interface InvoiceListParams {
  page?: number;
  pageSize?: number;
  status?: InvoiceStatus | 'all';
  origin?: 'uploaded' | 'created' | 'all';
  archived?: 'true' | 'false' | 'all';
  q?: string;
  from?: string;
  to?: string;
}

export interface InvoiceListResponse {
  items: Invoice[];
  total: number;
  page: number;
  pageSize: number;
}

export async function listInvoices(params: InvoiceListParams): Promise<InvoiceListResponse> {
  return unwrap(
    api.get<ApiEnvelope<InvoiceListResponse>>('/invoices', { params }),
  );
}

export async function getInvoice(id: string): Promise<Invoice> {
  return unwrap(api.get<ApiEnvelope<Invoice>>(`/invoices/${id}`));
}

export async function patchInvoice(id: string, patch: Partial<Invoice>): Promise<Invoice> {
  return unwrap(api.patch<ApiEnvelope<Invoice>>(`/invoices/${id}`, patch));
}

export async function deleteInvoice(id: string): Promise<void> {
  await api.delete(`/invoices/${id}`);
}

export async function reprocessInvoice(id: string): Promise<Invoice> {
  return unwrap(api.post<ApiEnvelope<Invoice>>(`/invoices/${id}/reprocess`));
}

export async function uploadInvoices(files: File[], onProgress?: (loaded: number, total: number) => void): Promise<Invoice[]> {
  const fd = new FormData();
  for (const f of files) fd.append('files', f, f.name);
  const res = await api.post<ApiEnvelope<{ items: Invoice[] }>>('/invoices/upload', fd, {
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(e.loaded, e.total);
    },
  });
  if (!res.data.ok) throw new Error(res.data.error.message);
  return res.data.data.items;
}

export async function exportInvoice(id: string, format: ExportFormat): Promise<Blob> {
  const res = await api.post(`/invoices/${id}/export`, null, {
    params: { format },
    responseType: 'blob',
  });
  return res.data as Blob;
}

export async function exportInvoicesZip(id: string, formats: ExportFormat[]): Promise<Blob> {
  const res = await api.post(`/invoices/${id}/export`, { formats }, {
    responseType: 'blob',
  });
  return res.data as Blob;
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function getOriginalFileUrl(id: string): string {
  const base = import.meta.env.VITE_API_BASE_URL || '/api/v1';
  return `${base}/invoices/${id}/file`;
}

/**
 * Fetches the original file as a Blob using the authenticated axios instance.
 * Use this to build an object URL that iframes/img tags can consume (browsers
 * don't attach Authorization headers to `src` attributes).
 */
export async function fetchOriginalFileBlob(id: string): Promise<Blob> {
  const res = await api.get(`/invoices/${id}/file`, { responseType: 'blob' });
  return res.data as Blob;
}

/* ---------------------- Manual builder + send helpers ---------------------- */

export interface CreateInvoicePayload {
  vendor: Invoice['vendor'];
  buyer:  Invoice['buyer'];
  invoiceNumber: string;
  issueDate?: string;
  dueDate?: string | null;
  currency: string;
  lineItems: Invoice['lineItems'];
  subtotal: number;
  taxTotal: number;
  total: number;
  paymentTerms?: string | null;
  notes?: string | null;
  theme: string;
  accentColor: string;
  clientId?: string | null;
  /** When true, persists with status='draft'. The invoice stays in the Drafts
   *  tab until the user explicitly finalizes it. */
  draft?: boolean;
}

export async function createInvoice(body: CreateInvoicePayload): Promise<Invoice> {
  return unwrap(api.post<ApiEnvelope<Invoice>>('/invoices/create', body));
}

export async function sendInvoiceEmail(id: string, body: { to: string; subject?: string; message?: string }): Promise<void> {
  const res = await api.post<ApiEnvelope<{ ok: true }>>(`/invoices/${id}/send-email`, body);
  if (!res.data.ok) throw new Error(res.data.error.message);
}

export async function getNextInvoiceNumber(): Promise<string> {
  return (await unwrap(api.get<ApiEnvelope<{ invoiceNumber: string }>>('/invoices/next-number'))).invoiceNumber;
}

export async function finalizeInvoice(id: string): Promise<Invoice> {
  return unwrap(api.post<ApiEnvelope<Invoice>>(`/invoices/${id}/finalize`));
}

export async function archiveInvoice(id: string): Promise<Invoice> {
  return unwrap(api.post<ApiEnvelope<Invoice>>(`/invoices/${id}/archive`));
}

export async function unarchiveInvoice(id: string): Promise<Invoice> {
  return unwrap(api.post<ApiEnvelope<Invoice>>(`/invoices/${id}/unarchive`));
}
